import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
    Incidencia,
    IncidenciaEstatus,
    IncidenciaPagination,
    IncidenciaStats,
    IncidenciaTipo,
} from 'app/modules/admin/apps/incidencia/incidencia.types';
import { environment } from 'environments/environment';
import { BehaviorSubject, Observable, tap } from 'rxjs';

export interface IncidenciaFilter {
    tipo?: IncidenciaTipo;
    estatus?: IncidenciaEstatus;
    entidad?: string;
    registroId?: number;
    pacienteId?: number;
    celular?: string;
}

@Injectable({ providedIn: 'root' })
export class IncidenciaService {
    baseUrl = `${environment.urlBase}incidencia`;

    private _pagination = new BehaviorSubject<IncidenciaPagination | null>(null);
    private _items = new BehaviorSubject<Incidencia[] | null>(null);
    private _stats = new BehaviorSubject<IncidenciaStats | null>(null);

    get pagination$(): Observable<IncidenciaPagination | null> {
        return this._pagination.asObservable();
    }
    get items$(): Observable<Incidencia[] | null> {
        return this._items.asObservable();
    }
    get stats$(): Observable<IncidenciaStats | null> {
        return this._stats.asObservable();
    }

    constructor(private _http: HttpClient) {}

    getIncidencias(
        page: number = 0,
        size: number = 20,
        filter: IncidenciaFilter = {},
    ): Observable<{ pagination: IncidenciaPagination; items: Incidencia[] }> {
        let params = new HttpParams()
            .set('page', String(page))
            .set('size', String(size));
        if (filter.tipo) params = params.set('tipo', filter.tipo);
        if (filter.estatus) params = params.set('estatus', filter.estatus);
        if (filter.entidad) params = params.set('entidad', filter.entidad);
        if (filter.registroId != null) params = params.set('registroId', String(filter.registroId));
        if (filter.pacienteId != null) params = params.set('pacienteId', String(filter.pacienteId));
        if (filter.celular) params = params.set('celular', filter.celular);

        return this._http
            .get<{ pagination: IncidenciaPagination; items: Incidencia[] }>(this.baseUrl, { params })
            .pipe(
                tap((resp) => {
                    this._pagination.next(resp.pagination);
                    this._items.next(resp.items);
                }),
            );
    }

    getById(id: number): Observable<Incidencia> {
        return this._http.get<Incidencia>(`${this.baseUrl}/${id}`);
    }

    getStats(): Observable<IncidenciaStats> {
        return this._http
            .get<IncidenciaStats>(`${this.baseUrl}/stats`)
            .pipe(tap((stats) => this._stats.next(stats)));
    }

    resolver(id: number, pacienteId: number, nota: string): Observable<Incidencia> {
        return this._http.post<Incidencia>(`${this.baseUrl}/${id}/resolver`, {
            pacienteId,
            nota,
        });
    }

    descartar(id: number, nota: string): Observable<Incidencia> {
        return this._http.post<Incidencia>(`${this.baseUrl}/${id}/descartar`, {
            nota,
        });
    }
}
